import { api } from "@/api";
import { resolveContentDispositionFileName } from "@/utils/fileDownload";
import type {
  FidexaPayAvailability,
  FidexaPayCharge,
  SetReconciledRequest,
  FidexaPayOtpChallenge,
  RequestFidexaPayOtpRequest,
  StatementQuery,
  StatementResponse,
  UpdatePixKeyRequest,
  WalletResponse,
  Withdrawal,
  WithdrawalDetailResponse,
  WithdrawalsQuery,
  WithdrawalsResponse,
} from "./fidexaPayWalletTypes";

// companyId vai explícito na URL, mesmo padrão de flowPaymentSubaccountServices.
const base = (companyId: string) => `v1/flow-payments/${companyId}`;

const statementParams = (query: StatementQuery) => ({
  start_date: query.startDate || undefined,
  end_date: query.endDate || undefined,
  reconciliation:
    query.reconciliation && query.reconciliation !== "all"
      ? query.reconciliation
      : undefined,
  withdrawal:
    query.withdrawal && query.withdrawal !== "all"
      ? query.withdrawal
      : undefined,
  customer_name: query.customerName || undefined,
  document: query.document || undefined,
  page: query.page,
  page_size: query.pageSize,
});

export type DownloadedFile = { blob: Blob; fileName: string };

const download = async (
  url: string,
  params: Record<string, unknown> | undefined,
  fallbackName: string
): Promise<DownloadedFile> => {
  const response = await api.get(url, { params, responseType: "blob" });
  const fileName =
    resolveContentDispositionFileName(
      response.headers["content-disposition"] as string | undefined
    ) ?? fallbackName;

  return { blob: response.data as Blob, fileName };
};

const getWallet = async (companyId: string): Promise<WalletResponse> => {
  const { data } = await api.get<WalletResponse>(`${base(companyId)}/wallet`);
  return data;
};

const getStatement = async (
  companyId: string,
  query: StatementQuery
): Promise<StatementResponse> => {
  const { data } = await api.get<StatementResponse>(
    `${base(companyId)}/statement`,
    { params: statementParams(query) }
  );
  return data;
};

const exportStatement = (companyId: string, query: StatementQuery) =>
  download(
    `${base(companyId)}/statement/export`,
    { ...statementParams(query), page: undefined, page_size: undefined },
    "fidexa-pay-extrato.csv"
  );

/** Interruptor da linha do extrato: vale para todas as parcelas do Pix. */
const setReconciled = async (
  companyId: string,
  chargeId: string,
  reconciled: boolean
): Promise<void> => {
  await api.put(`${base(companyId)}/statement/${chargeId}/reconciliation`, {
    reconciled,
    origin: "statement",
  });
};

const getCharge = async (
  companyId: string,
  chargeId: string
): Promise<FidexaPayCharge> => {
  const { data } = await api.get<FidexaPayCharge>(
    `${base(companyId)}/charges/${chargeId}`
  );
  return data;
};

const setInstallmentReconciled = async (
  companyId: string,
  chargeId: string,
  installmentId: string,
  request: SetReconciledRequest
): Promise<FidexaPayCharge> => {
  const { data } = await api.put<FidexaPayCharge>(
    `${base(companyId)}/charges/${chargeId}/installments/${installmentId}/reconciliation`,
    request
  );
  return data;
};

const getWithdrawals = async (
  companyId: string,
  query: WithdrawalsQuery
): Promise<WithdrawalsResponse> => {
  const { data } = await api.get<WithdrawalsResponse>(
    `${base(companyId)}/withdrawals`,
    {
      params: {
        start_date: query.startDate || undefined,
        end_date: query.endDate || undefined,
        page: query.page,
        page_size: query.pageSize,
      },
    }
  );
  return data;
};

const getWithdrawal = async (
  companyId: string,
  withdrawalId: string
): Promise<WithdrawalDetailResponse> => {
  const { data } = await api.get<WithdrawalDetailResponse>(
    `${base(companyId)}/withdrawals/${withdrawalId}`
  );
  return data;
};

const exportWithdrawal = (companyId: string, withdrawalId: string) =>
  download(
    `${base(companyId)}/withdrawals/${withdrawalId}/export`,
    undefined,
    "fidexa-pay-saque.csv"
  );

const requestOtp = async (
  companyId: string,
  request: RequestFidexaPayOtpRequest
): Promise<FidexaPayOtpChallenge> => {
  const { data } = await api.post<FidexaPayOtpChallenge>(
    `${base(companyId)}/otp`,
    request
  );
  return data;
};

const createWithdrawal = async (
  companyId: string,
  otpCode: string
): Promise<Withdrawal> => {
  const { data } = await api.post<Withdrawal>(
    `${base(companyId)}/withdrawals`,
    {
      otp_code: otpCode,
    }
  );
  return data;
};

const updatePixKey = async (
  companyId: string,
  request: UpdatePixKeyRequest
): Promise<void> => {
  await api.put(`${base(companyId)}/withdrawal-pix-key`, request);
};

type AvailabilityResponse = {
  enabled: boolean;
  company_ids: string[] | null;
};

const getAvailability = async (): Promise<FidexaPayAvailability> => {
  const { data } = await api.get<AvailabilityResponse>(
    "v1/flow-payments/availability"
  );
  return {
    enabled: Boolean(data?.enabled),
    companyIds: data?.company_ids ?? [],
  };
};

export const fidexaPayWalletServices = {
  getAvailability,
  getWallet,
  getStatement,
  exportStatement,
  setReconciled,
  getCharge,
  setInstallmentReconciled,
  getWithdrawals,
  getWithdrawal,
  exportWithdrawal,
  requestOtp,
  createWithdrawal,
  updatePixKey,
};
